/**
 * Pure helpers behind the homepage free-training slide-in
 * (components/premium/HomeTrainingCard.js). Kept out of the component so the
 * timing and storage rules are testable without rendering.
 */

// A slot closer than this is skipped rather than offered: a visitor who is
// still typing their email when it starts arrives at a session in progress.
export const MIN_SIGNUP_LEAD_MS = 30 * 1000;

export const SNOOZE_KEY = 'canira.homeTraining.snoozedUntil';
export const REGISTERED_KEY = 'canira.homeTraining.registeredUntil';
export const SNOOZE_DAYS = 3;
export const REGISTERED_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The first offered start time still at least `leadMs` away, or null. */
export function nextSlot(times, nowMs = Date.now(), leadMs = MIN_SIGNUP_LEAD_MS) {
  if (!Array.isArray(times)) return null;
  for (const iso of times) {
    const t = Date.parse(iso);
    if (Number.isFinite(t) && t - nowMs >= leadMs) return iso;
  }
  return null;
}

/** Remaining time split for the countdown boxes. Never negative. */
export function countdownParts(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  return {
    hours: Math.floor(total / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

export const pad2 = (n) => String(n).padStart(2, '0');

function dayStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * "Today · 9:30 AM EDT", "Tomorrow · …", or "Thu, Oct 2 · …", in the
 * VISITOR's own timezone — the zone is named because a time with no zone is
 * the one thing a registrant can't act on.
 */
export function slotLabel(iso, now = new Date(), locale) {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  let time;
  try {
    time = when.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  } catch {
    time = when.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  }
  const days = Math.round((dayStart(when) - dayStart(now)) / DAY_MS);
  let day;
  if (days === 0) day = 'Today';
  else if (days === 1) day = 'Tomorrow';
  else day = when.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${day} · ${time}`;
}

/** Past the first screenful — the visitor has read the hero and kept going. */
export function shouldReveal(scrollY, viewportHeight) {
  const vh = Number(viewportHeight) || 800;
  return Number(scrollY) > vh * 0.6;
}

// Storage throws in private mode and with site data blocked; every access is
// wrapped, and "can't tell" means "not snoozed" — the card is a closable
// offer, not something to hide on a storage error.
function readUntil(storage, key) {
  try {
    const v = Number(storage?.getItem(key));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

function writeUntil(storage, key, until) {
  try {
    storage?.setItem(key, String(until));
  } catch {
    // Remembering is a courtesy; the close still works for this page view.
  }
}

export function isHidden(storage, nowMs = Date.now()) {
  return readUntil(storage, SNOOZE_KEY) > nowMs || readUntil(storage, REGISTERED_KEY) > nowMs;
}

export function snooze(storage, nowMs = Date.now()) {
  writeUntil(storage, SNOOZE_KEY, nowMs + SNOOZE_DAYS * DAY_MS);
}

export function markRegistered(storage, nowMs = Date.now()) {
  writeUntil(storage, REGISTERED_KEY, nowMs + REGISTERED_DAYS * DAY_MS);
}

/**
 * The register route answers in `message`, and its words are written for
 * people but not for this card. Map the few that matter; everything else
 * gets one honest sentence rather than a raw server string.
 */
export function registrationErrorCopy(status, body) {
  const msg = typeof body?.message === 'string' ? body.message : '';
  if (/already registered/i.test(msg)) {
    return "You're already registered — your watch link is in your inbox.";
  }
  if (status === 429) return 'Too many tries from this connection. Wait a minute and try again.';
  if (body?.code === 'WEBINAR_PAUSED' || /not enabled|deadline|full/i.test(msg)) {
    return "This training isn't taking sign-ups right now.";
  }
  return "We couldn't save your seat. Please try again.";
}

export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const looseText = (s) => String(s || '')
  .toLowerCase()
  .replace(/[‘’“”'"`]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * The description, or null when it only repeats the title. A webinar's
 * description is often "Join <title> hosted by <host>", which on a card that
 * already shows the title in bold is a second copy of it costing three lines.
 */
export function cardBlurb(blurb, title) {
  const b = looseText(blurb);
  if (!b) return null;
  const t = looseText(title);
  if (t && b.includes(t)) return null;
  return String(blurb).trim();
}

/** The countdown as one clock reading: "06:33", or "2:06:33" past an hour. */
export function countdownClock(ms) {
  const { hours, minutes, seconds } = countdownParts(ms);
  return hours > 0
    ? `${hours}:${pad2(minutes)}:${pad2(seconds)}`
    : `${pad2(minutes)}:${pad2(seconds)}`;
}
